"use client";
import Link from "next/link";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useEffect, useState } from "react";

interface FooterLink {
  id: number;
  url: string;
  newTab: boolean;
  text: string;
}

interface Destination {
  text: string;
  url_link: string;
}

function FooterLinkItem({
  url,
  text,
  newTab,
  currentPath,
}: FooterLink & { currentPath: string | null }) {
  return (
    <Link
      href={url}
      target={newTab ? "_blank" : undefined}
      rel={newTab ? "noopener noreferrer" : undefined}
      className={`hover:dark:text-violet-400 ${
        currentPath === url && "dark:text-violet-400 dark:border-violet-400"
      }`}
    >
      {text}
    </Link>
  );
}

export default function Footer({
  footerText,
  legalLinks,
  socialLinks,
  topDestinations,
}: {
  footerText: string;
  legalLinks: Array<FooterLink>;
  socialLinks: Array<FooterLink>;
  topDestinations: Array<Destination>;
}) {
  const [currentPath, setCurrentPath] = useState<string | null>(null);

  useEffect(() => {
    setCurrentPath(window.location.pathname);
  }, []);

  return (
    <footer
      className="bg-blue w-full py-3 px-4 dt:p-[8.5rem] dt:flex text-white"
      id="footer"
    >
      <div className="w-full dt:w-[40%] dt:grid" id="footerText">
        <Markdown
          remarkPlugins={[remarkGfm]}
          className="tracking-wider font-bold uppercase text-1 leading-1 dt:text-1.5 dt:leading-1.5"
        >
          {footerText}
        </Markdown>
        <span className="hidden dt:block self-end">
          © Coach America {new Date().getFullYear()}
        </span>
      </div>
      <div className="mt-3 dt:mt-0 flex w-full dt:w-[60%]">
        <div className="dt:w-2/3 dt:pl-1 pr-1/2 dt:pr-0">
          <h2 className="mb-1/2 uppercase font-bold text-3/4 leading-1 tracking-widest">
            <span className="hidden dt:inline-block">Top</span> Destinations
          </h2>
          <ul className="dt:columns-2">
            {topDestinations.map((destination: Destination) => (
              <li className="mb-1/2" key={destination.url_link}>
                {destination.url_link && destination.url_link.length > 0 ? (
                  <a
                    rel="noopener noreferrer"
                    href={destination.url_link}
                    title={destination.text}
                    className={`${
                      destination.url_link && destination.url_link.length > 0
                        ? "font-bold underline hover:no-underline "
                        : ""
                    }`}
                  >
                    {destination.text}
                  </a>
                ) : (
                  destination.text
                )}
              </li>
            ))}
          </ul>
        </div>
        <div className="pl-1/2 dt:pl-1 relative">
          <h2 className="mb-1/2 uppercase font-bold text-3/4 leading-1 tracking-widest">
            Social
          </h2>
          <ul>
            {socialLinks.map((link: FooterLink) => {
              return (
                <li className="mb-1/2" key={link.id}>
                  <a
                    rel="noopener noreferrer"
                    href={link.url}
                    title={link.text}
                    target={link.newTab ? "_blank" : "_self"}
                    className="hover:underline"
                  >
                    {link.text}
                  </a>
                </li>
              );
            })}
          </ul>
          <h2 className="mb-1/2 mt-2 uppercase font-bold text-3/4 leading-1 tracking-widest">
            Legal
          </h2>
          <ul>
            {legalLinks.map((link: FooterLink) => (
              <li className="mb-1/2" key={link.id}>
                <FooterLinkItem
                  url={link.url}
                  text={link.text}
                  id={link.id}
                  newTab={link.newTab}
                  currentPath={currentPath}
                />
              </li>
            ))}
          </ul>
          <h2 className="mb-1/2 mt-2 uppercase font-bold text-3/4 leading-1 tracking-widest">
            Address
          </h2>
          <a
            href="https://maps.app.goo.gl/2nhM4y2UBi7pYitf7"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:underline"
          >
            <address className="not-italic text-sm leading-relaxed">
              101 Crawfords Corner Rd<br />
              #4331<br />
              Holmdel, NJ 07733
            </address>
          </a>
        </div>
      </div>

      <div className="dt:hidden mt-1 text-1 leading-1 ">
        © Coach America {new Date().getFullYear()}
      </div>
    </footer>
  );
}
